import { useState, useMemo } from "react";
import styled from "styled-components/macro";
import Stack from "../Stack";
import Button from "../Button";
import Automation from "./Automation";
import { useSkipioApi } from "../../api";
import useLoadAll from "../../hooks/use-load-all";
import { transformKey, caseMethods } from "../../api/case";
import { newAutomation } from "./factories";
import usePlan from "../../hooks/use-plan";
import usePaywall from "../../hooks/use-paywall";
import { useSkipioQuery } from "../../hooks/use-query";

function snakeCaseTopLevel(object) {
  const clone = {};
  for (let [key, value] of Object.entries(object)) {
    clone[transformKey(key, caseMethods.snake)] = value;
  }
  return clone;
}

function AutomationsEditor({ automationAction }) {
  const skipioApi = useSkipioApi();
  const plan = usePlan();
  const showPaywall = usePaywall();

  const automationMeta = useSkipioQuery({
    queryKey: ["automations", "meta"],
  }).data;
  const { data: rawAutomations } = useLoadAll(skipioApi.automations, {
    ...Object.fromEntries(
      Object.entries(automationAction).filter(([_, value]) => value)
    ),
  });

  // Filter out automations that partially match this action.
  // so if we're looking for statusTypeId and typeId, filter
  // those with that do not match every property
  const automations = rawAutomations.filter((automation) => {
    return automation.automationActions.every((action) => {
      return Object.keys(automationAction).every((key) => {
        return action[key] === automationAction[key];
      });
    });
  });

  const [drafts, setDrafts] = useState([]);

  const actions = useMemo(() => snakeCaseTopLevel(automationMeta.actions), [
    automationMeta,
  ]);

  const availableActions = Object.entries(
    actions
  ).filter(([_, actionDefinition]) =>
    Object.keys(actionDefinition.fields).every((key) => key in automationAction)
  );

  const addNewAutomation = () => {
    if (!plan.canMakeRuleBasedAutomation) {
      showPaywall(
        "Looks like you're ready to add another automation. Great! Let's upgrade your account."
      );
      return;
    }

    setDrafts((drafts) => {
      return [
        newAutomation({
          // availableActions is an entries array like:
          // [['add_to_campaign',{label: "Add to campaign"}]]
          name: availableActions[0][0],
          ...automationAction,
        }),
        ...drafts,
      ];
    });
  };

  return (
    <Container>
      <Stack>
        <Right>
          <Button onClick={addNewAutomation}>Create New Automation</Button>
        </Right>
        {drafts.map((automation) => (
          <Automation
            key={automation.tempId}
            automation={automation}
            automationAction={automationAction}
            onSave={() =>
              setDrafts((drafts) =>
                drafts.filter((a) => a.tempId !== automation.tempId)
              )
            }
          />
        ))}
        {automations.map((automation) => (
          <Automation
            key={automation.id}
            automation={automation}
            automationAction={automationAction}
            onSave={() => {}}
          />
        ))}
      </Stack>
    </Container>
  );
}

const Container = styled.div`
  text-align: center;
  margin: auto;
  max-width: 600px;
`;

const Right = styled.div`
  text-align: right;
`;

export default AutomationsEditor;
